import { ResourceType, type InventoryResource } from '@repo/api-gw-sdk';

/**
 * Checks if a resource uses native backup (e.g., FSx/EFS use AWS Backup in customer account)
 * @param resource The inventory resource to check
 * @returns true if the resource uses native backup
 */
export const isNativeBackupResource = (
  resource: InventoryResource
): boolean => {
  return (
    resource.resourceType === ResourceType.AwsFsx ||
    resource.resourceType === ResourceType.AwsEfs ||
    resource.resourceType === ResourceType.AwsRedshift ||
    resource.resourceType === ResourceType.AwsDocumentdb ||
    resource.resourceType === ResourceType.AwsNeptune
  );
};

/**
 * Checks if a resource is FSx for NetApp ONTAP.
 * AWS Backup does not support cross-region copy for FSx ONTAP resources.
 * @param resource The inventory resource to check
 * @returns true if the resource is FSx ONTAP
 */
const isFsxOntapResource = (resource: InventoryResource): boolean => {
  return (
    resource.resourceType === ResourceType.AwsFsx &&
    resource.resourceProperties?.awsFsx?.fileSystemType === 'ONTAP'
  );
};

/**
 * Checks if a resource is FSx with INTELLIGENT_TIERING storage type.
 * AWS Backup does not support cross-region copy for FSx file systems with intelligent tiering
 * (applies to OpenZFS and Lustre).
 * @param resource The inventory resource to check
 * @returns true if the resource is FSx with INTELLIGENT_TIERING
 */
const isFsxIntelligentTiering = (resource: InventoryResource): boolean => {
  return (
    resource.resourceType === ResourceType.AwsFsx &&
    resource.resourceProperties?.awsFsx?.storageType === 'INTELLIGENT_TIERING'
  );
};

/**
 * Checks if a resource has FSx cross-region backup restrictions.
 * AWS Backup does not support cross-region copy for:
 * - FSx ONTAP (any storage type)
 * - FSx with INTELLIGENT_TIERING storage type (OpenZFS, Lustre)
 * @param resource The inventory resource to check
 * @returns true if cross-region backup is not supported
 */
const isFsxCrossRegionUnsupported = (resource: InventoryResource): boolean => {
  return isFsxOntapResource(resource) || isFsxIntelligentTiering(resource);
};

/**
 * Checks if a resource does not support cross-region backup via AWS Backup.
 * - Redshift (all types): cross-region copy not supported
 * - FSx ONTAP / FSx with INTELLIGENT_TIERING: cross-region copy not supported
 */
export const isNativeCrossRegionUnsupported = (
  resource: InventoryResource
): boolean => {
  return (
    resource.resourceType === ResourceType.AwsRedshift ||
    isFsxCrossRegionUnsupported(resource)
  );
};

/**
 * Checks if all resources in a collection use native backup
 * @param entities Array of inventory resources
 * @returns true if all resources use native backup
 */
export const areAllNativeBackupResources = (
  entities: InventoryResource[]
): boolean => {
  return entities.length > 0 && entities.every(isNativeBackupResource);
};

/**
 * Checks if a selection contains both native and non-native backup resources
 * @param entities Array of inventory resources
 * @returns true if the selection is mixed (contains both native and non-native resources)
 */
export const hasMixedNativeBackupSelection = (
  entities: InventoryResource[]
): boolean => {
  const hasNative = entities.some(isNativeBackupResource);
  const hasNonNative = entities.some(
    (entity) => !isNativeBackupResource(entity)
  );
  return hasNative && hasNonNative;
};
